import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { FORM_ELEMENT_EMPTY } from 'constants/FormConstants';
import { SortProps } from 'constants/TypeConstants';
import { Orders } from 'features/orders/services/getOrders';
import { TAG_APP_USERS } from 'services/TagsConstants';
import { store } from 'store/store';

import { createQueryString, handleDateAndTime } from '../../../utils/helpers';
import { AppUserParameters } from './getAppUserById';

const BASE_URL = '/app-users';

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
  build.query<AppUsersResponse, AppUserParameters>({
    async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
      const appUsersResponse = await fetchWithBQ(createQueryString(_arg, BASE_URL));
      const appUsersData = appUsersResponse.data as AppUsersResponse;
      const countriesContent = store.getState().auth.userCountry;
      const { dateFormat } = countriesContent;
      const { language } = store.getState().language;
      const appUsers = appUsersData?.content.map((appUser: AppUser) => {
        const createdAtFormatted = handleDateAndTime(appUser?.createdAt, dateFormat, language);
        const lastActiveAtFormatted = handleDateAndTime(
          appUser?.lastActiveAt,
          dateFormat,
          language,
        );
        const { formattedDate: dateCreatedAt, formattedTime: timeCreatedAt } = createdAtFormatted;
        const { formattedDate: dateLastActiveAt, formattedTime: timeLastActiveAt } =
          lastActiveAtFormatted;
        return {
          ...appUser,
          createdAt: `${dateCreatedAt}, ${timeCreatedAt}`,
          lastActiveAt: appUser?.lastActiveAt
            ? `${dateLastActiveAt}, ${timeLastActiveAt}`
            : FORM_ELEMENT_EMPTY,
        };
      });
      return {
        data: {
          content: appUsers,
          totalElements: appUsersData?.totalElements,
          totalPages: appUsersData?.totalPages,
        },
      };
    },
    providesTags: [TAG_APP_USERS],
  });

export type AppUsersParameters = AppUserParameters & {
  email?: string;
  page?: number;
  size?: number;
  sort?: SortProps;
};

export type AppUser = {
  address: string;
  blocked: boolean;
  city: string;
  country: string;
  createdAt: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastActiveAt?: string;
  lastName: string;
  orders: Orders;
  phoneNumber: string;
  phoneNumberCountryCode: string;
  phoneVerified: boolean;
  referralCode: string;
  totalMoneySaved: number;
  totalWeightSaved: number;
  uuid: string;
};

export type AppUsersResponse = {
  content: AppUser[];
  totalElements: number;
  totalPages: number;
};
